import { useId, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  subcontractRequestsApi, type ContractBasis, type IntacctVendor, type NteRateUnit, type SubcontractRequest, type SubcontractRequestPrefill,
} from '@/lib/apiClient';
import { Button } from '@/components';
import Combobox, { type ComboboxOption } from '@/components/Combobox';
import DateField from './DateField';
import { jobHref } from './contractsModel';
import {
  requestHref, emptyRequestForm, requestFormFrom, createBody, updatePatch, validateRequestForm,
  vendorDetailsTyped, vendorFilledNote, vendorOptions, withVendor,
  LD_FREQUENCY_OPTIONS, CONTRACT_BASIS_OPTIONS, NTE_RATE_UNIT_OPTIONS, isTimeAndMaterial, DESCRIBED_BY, DESCRIPTION_LABELS, type RequestForm, type YesNo,
} from './subcontractRequestModel';

/** The latest day the API accepts for a planned date (these are plans, so a future day is normal). */
const LATEST_DAY = '2100-12-31';

/** The Subcontractor box is a Combobox, whose own input look is merged over so it matches the `.ct-field` inputs beside it. */
const FIELD_LOOK = {
  padding: 'var(--ew-s-2) var(--ew-s-3)', border: '1px solid var(--ew-n-300)',
  fontSize: 'var(--ew-fs-body-ui)', lineHeight: 'var(--ew-lh-ui)',
} as const;

/** How long the vendor list is kept: it changes once a night, so one fetch serves a whole visit. */
const VENDOR_LIST_MINUTES = 10;

function TextField({ label, value, onChange, help, maxLength, inputMode, placeholder, type }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  help?: string;
  maxLength?: number;
  inputMode?: 'text' | 'decimal' | 'numeric' | 'email' | 'tel';
  placeholder?: string;
  type?: 'text' | 'email' | 'tel';
}) {
  return (
    <label className="ct-field">
      <span>{label}</span>
      <input
        type={type ?? 'text'}
        value={value}
        maxLength={maxLength}
        inputMode={inputMode}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {help && <span className="ct-help">{help}</span>}
    </label>
  );
}

function TextArea({ label, value, onChange, help, rows, maxLength }: {
  label: string; value: string; onChange: (v: string) => void; help?: string; rows: number; maxLength: number;
}) {
  return (
    <label className="ct-field">
      <span>{label}</span>
      <textarea rows={rows} maxLength={maxLength} value={value} onChange={(e) => onChange(e.target.value)} />
      {help && <span className="ct-help">{help}</span>}
    </label>
  );
}

/** Yes, No, or neither yet. Neither is not No: the API will not submit a request with a question left open. */
function YesNoField({ label, value, onChange }: { label: string; value: YesNo; onChange: (v: YesNo) => void }) {
  const name = useId();
  return (
    <fieldset className="ct-yn">
      <legend>{label}</legend>
      <div className="ct-yn-options">
        <label><input type="radio" name={name} checked={value === 'yes'} onChange={() => onChange('yes')} /> Yes</label>
        <label><input type="radio" name={name} checked={value === 'no'} onChange={() => onChange('no')} /> No</label>
      </div>
    </fieldset>
  );
}

/**
 * The subcontract request form (#627): a page, not a modal, because it has about forty fields and a long scope.
 * It starts a new request from a job's facts, or changes one that exists.
 *
 * Everything may be left half-done: a request is saved as a draft, and the page it saves to (the request's own)
 * says what is still missing and offers Submit. What counts as complete is the API's, so this form requires
 * nothing but a template; it only refuses a value that cannot be what was meant.
 *
 * Saving an existing request sends only the fields that changed since the form opened, so a colleague's edit to
 * another field, made while this was open, survives. A field whose answer makes it not apply (a bond's amount when
 * there is no bond) is cleared with it, so what is stored never disagrees with the answers beside it.
 */
export default function SubcontractRequestForm({ prefill, request }: {
  prefill: SubcontractRequestPrefill;
  /** The request being changed; omitted to start one. */
  request?: SubcontractRequest;
}) {
  const editing = !!request;
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Fixed for the life of the form: what it opened with is what a change is measured against.
  const [initial] = useState<RequestForm>(() => (request ? requestFormFrom(request, prefill) : emptyRequestForm(prefill)));
  const [form, setForm] = useState<RequestForm>(initial);
  const [error, setError] = useState<string | null>(null);

  // A new request is a subcontract about to be booked, so the API refuses a placeholder-number or archived job, and
  // anyone who may not start one. Say so before the person fills in forty fields.
  const blockedWhy = editing ? null
    : prefill.access.archived ? 'This job is archived, so a new subcontract can’t be requested on it.'
    : prefill.access.placeholderNumber ? 'This job doesn’t have a real job number from accounting, so a new subcontract can’t be requested on it.'
    : !prefill.access.canRequest ? 'Your account can’t request subcontracts.'
    : null;

  // Intacct's vendor list for the Subcontractor box (#627). ⚠️ IT CAN NEVER BLOCK THE FORM: while it loads, if it fails, or while it
  // is empty (the nightly vendor sync ships off), the box is the plain text box it always was. Its key is outside ['contracts'] on
  // purpose: saving a request invalidates that prefix, and the list is about 1,200 vendors that change once a night.
  const vendorList = useQuery({
    queryKey: ['vendors', 'intacct'],
    queryFn: subcontractRequestsApi.vendors,
    staleTime: VENDOR_LIST_MINUTES * 60_000,
    enabled: !blockedWhy,
  });
  const vendors = vendorList.data?.vendors;
  const vendorChoices = useMemo(() => vendorOptions(vendors ?? []), [vendors]);
  const vendorById = useMemo(() => new Map((vendors ?? []).map((v) => [v.id, v])), [vendors]);
  // The vendor the last pick filled in: what the note under the box describes, and what "nothing typed since" is measured against.
  const [picked, setPicked] = useState<IntacctVendor | null>(null);
  const vendorNameId = useId();

  const set = <K extends keyof RequestForm>(key: K, value: RequestForm[K]) => setForm((f) => ({ ...f, [key]: value }));
  const setAnswer = (key: string, value: YesNo) => setForm((f) => ({ ...f, requirements: { ...f.requirements, [key]: value } }));
  const setDescription = (line: keyof RequestForm['descriptions'], value: string) =>
    setForm((f) => ({ ...f, descriptions: { ...f.descriptions, [line]: value } }));

  const save = useMutation({
    mutationFn: async (): Promise<SubcontractRequest> => {
      if (request) {
        const patch = updatePatch(initial, form);
        if (patch.templateKey === undefined && patch.payload === undefined) return request;
        return subcontractRequestsApi.update(request.id, patch);
      }
      return subcontractRequestsApi.create(createBody(form, prefill.project.id));
    },
    onSuccess: (saved) => {
      // The request's own page opens next: hand it what was just saved, so it never flashes the old entries while it refetches.
      qc.setQueryData(['contracts', 'request', saved.id], saved);
      void qc.invalidateQueries({ queryKey: ['contracts'] });
      // Replace the form's place in the history: Back from the request page must not return to a form that, filled in
      // and saved again, would make a second draft.
      navigate(requestHref(saved.id), { replace: true });
    },
    onError: (e) => setError((e as Error)?.message || 'That did not save. Try again.'),
  });

  /**
   * A vendor was picked from the list. It replaces the WHOLE subcontractor block (so a half-typed address is never left under a
   * different vendor's name), and asks first only when that would throw away something a person typed. Declining leaves the form
   * exactly as it was, the name box included: the list's `onPick` replaces the default write of the name, so nothing has changed yet.
   * "New to us" is never touched: being in Intacct does not say the new vendor form is done.
   */
  const pickVendor = (option: ComboboxOption) => {
    const vendor = option.key ? vendorById.get(option.key) : undefined;
    if (!vendor) { set('vendorName', option.value); return; }
    if (vendorDetailsTyped(form, picked) && !window.confirm(`Replace what you typed with Intacct’s details for ${vendor.name}?`)) return;
    setForm((f) => withVendor(f, vendor));
    setPicked(vendor);
  };

  const onSave = () => {
    const problem = validateRequestForm(form);
    if (problem) { setError(problem); return; }
    setError(null);
    save.mutate();
  };

  const timeAndMaterial = isTimeAndMaterial(form.contractBasis);
  // A new request is a draft from its first save, and so is one that has not been submitted yet.
  const isDraft = !editing || request?.status === 'draft';
  const project = prefill.project;
  const jobLabel = `${project.jobNumber}${project.title ? ` · ${project.title}` : ''}`;
  const backTo = request ? requestHref(request.id) : jobHref(project.id);
  const suggested = prefill.templates.find((t) => t.key === prefill.suggestedTemplate);

  if (blockedWhy) {
    return (
      <div className="ct-page">
        <Link className="ct-back" to={backTo}>← Back to job {project.jobNumber}</Link>
        <div className="ct-hint" role="note">{blockedWhy}</div>
      </div>
    );
  }

  const ownerNote = prefill.ownerContract && !prefill.ownerContract.complete
    ? (prefill.ownerContract.tracked
      ? 'An owner contract is being tracked for this job but isn’t marked complete yet. An executed owner contract should be in the job file before a subcontract goes out.'
      : 'No owner contract is being tracked for this job. An executed owner contract should be in the job file before a subcontract goes out.')
    : null;

  return (
    <div className="ct-page">
      <Link className="ct-back" to={backTo}>{request ? '← Back to the request' : `← Back to job ${project.jobNumber}`}</Link>
      <div className="ct-page-head">
        <h1>{editing ? 'Edit subcontract request' : 'Request a subcontract'}</h1>
        <span className="ct-help">Job {jobLabel}</span>
      </div>
      {ownerNote && <div className="ct-hint" role="note">{ownerNote}</div>}
      {isDraft && (
        <div className="ct-hint">
          {editing
            ? 'This is a draft. Save it as often as you like and finish it later; you submit it from its own page once nothing is missing.'
            : 'You don’t have to finish this in one sitting. Save a draft at any point with the button at the bottom, and pick it up again from this job’s Contracts tab.'}
        </div>
      )}
      {request && request.status === 'submitted' && (
        <div className="ct-hint" role="note">
          This request has been submitted. Changes are saved straight away and show in the next Word document made from it.
        </div>
      )}

      <section className="ct-rq-section" aria-labelledby="rq-template">
        <h2 id="rq-template">Template</h2>
        <label className="ct-field">
          <span>Word template</span>
          <select value={form.templateKey} onChange={(e) => set('templateKey', e.target.value)}>
            <option value="">Choose a template…</option>
            {prefill.templates.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <span className="ct-help">
            {suggested
              ? `Suggested from the owner and the job’s state: ${suggested.label}. You can change it.`
              : 'The owner and the job’s state did not decide this one, so choose the template.'}
          </span>
        </label>
      </section>

      <section className="ct-rq-section" aria-labelledby="rq-job">
        <h2 id="rq-job">Job, as it should read on the contract</h2>
        <div className="ct-form">
          <div className="ct-grid2">
            <TextField label="Job name" value={form.jobName} maxLength={200} onChange={(v) => set('jobName', v)} />
            <TextField label="Cost code" value={form.costCode} maxLength={30} onChange={(v) => set('costCode', v)} />
          </div>
          <TextField label="Job address" value={form.jobAddress} maxLength={200} onChange={(v) => set('jobAddress', v)} />
          <div className="ct-grid3">
            <TextField label="Job city" value={form.jobCity} maxLength={100} onChange={(v) => set('jobCity', v)} />
            <TextField
              label="Job state" value={form.jobState} maxLength={20} onChange={(v) => set('jobState', v)}
              help="Two-letter code. It decides the Kentucky retainage wording on some templates."
            />
            <TextField label="Job zip" value={form.jobZip} maxLength={10} inputMode="numeric" onChange={(v) => set('jobZip', v)} />
          </div>
          <div className="ct-grid2">
            <TextField label="Owner" value={form.owner} maxLength={200} onChange={(v) => set('owner', v)} />
            <TextField label="Architect" value={form.architect} maxLength={200} onChange={(v) => set('architect', v)} help="Enter N/A if the job has none." />
          </div>
        </div>
      </section>

      <section className="ct-rq-section" aria-labelledby="rq-vendor">
        <h2 id="rq-vendor">Subcontractor</h2>
        <div className="ct-form">
          <div className="ct-field">
            <label htmlFor={vendorNameId}>Subcontractor name</label>
            {/* Always a Combobox, never swapped for a plain input when the list arrives: a swap remounts the input and drops the
                focus of someone already typing. With no options it IS a plain input. */}
            <Combobox
              id={vendorNameId} value={form.vendorName} onChange={(v) => set('vendorName', v)}
              options={vendorChoices} onPick={pickVendor} maxLength={200} inputStyle={FIELD_LOOK}
            />
            {picked && form.vendorName === picked.name
              ? <span className="ct-help" role="status">{vendorFilledNote(picked)}</span>
              : vendorList.isError
                ? <span className="ct-help">The Intacct vendor list isn’t available right now. Type the details.</span>
                : vendorChoices.length > 0
                  ? <span className="ct-help">Start typing to pick the vendor from Intacct, or type a name that isn’t there.</span>
                  : null}
          </div>
          <div style={{ display: 'grid', gap: 'var(--ew-s-1)' }}>
            <label className="ct-field ct-field--check">
              <input type="checkbox" checked={form.newVendor} onChange={(e) => set('newVendor', e.target.checked)} />
              <span>New to us: the new vendor form is asked for on the Contracts page</span>
            </label>
            {vendorChoices.length > 0 && <span className="ct-help">Being in Intacct doesn’t mean the new vendor form is done.</span>}
          </div>
          <TextField label="Subcontractor street address" value={form.vendorStreet} maxLength={200} onChange={(v) => set('vendorStreet', v)} />
          <div className="ct-grid3">
            <TextField label="Subcontractor city" value={form.vendorCity} maxLength={100} onChange={(v) => set('vendorCity', v)} />
            <TextField label="Subcontractor state" value={form.vendorState} maxLength={20} onChange={(v) => set('vendorState', v)} />
            <TextField label="Subcontractor zip" value={form.vendorZip} maxLength={10} inputMode="numeric" onChange={(v) => set('vendorZip', v)} />
          </div>
          <div className="ct-grid3">
            <TextField label="Subcontractor contact" value={form.contactName} maxLength={120} onChange={(v) => set('contactName', v)} />
            <TextField label="Subcontractor contact email" value={form.contactEmail} type="email" inputMode="email" maxLength={254} onChange={(v) => set('contactEmail', v)} />
            <TextField label="Subcontractor contact phone" value={form.contactPhone} type="tel" inputMode="tel" maxLength={40} onChange={(v) => set('contactPhone', v)} />
          </div>
        </div>
      </section>

      <section className="ct-rq-section" aria-labelledby="rq-price">
        <h2 id="rq-price">Agreement and price</h2>
        <div className="ct-form">
          <div className="ct-grid2">
            <DateField label="Agreement date" value={form.agreementDate} max={LATEST_DAY} onChange={(v) => set('agreementDate', v)} />
            <label className="ct-field">
              <span>Contract basis</span>
              <select value={form.contractBasis} onChange={(e) => set('contractBasis', e.target.value as ContractBasis)}>
                {CONTRACT_BASIS_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
              <span className="ct-help">
                {timeAndMaterial
                  ? 'Adds “Time and Material Not to Exceed basis, final contract amount may vary based on actual hours and materials purchased.” to the price section.'
                  : 'A fixed price. Choose time and material when the final amount will depend on actual hours and materials.'}
              </span>
            </label>
            <TextField
              label={timeAndMaterial ? 'Not-to-exceed amount ($)' : 'Subcontract amount ($)'} value={form.amount} inputMode="decimal"
              onChange={(v) => set('amount', v)} help="Dollars, like 4,680.00."
            />
            <TextField label="Retainage (%)" value={form.retainagePercent} inputMode="decimal" onChange={(v) => set('retainagePercent', v)} help="A percentage, like 10." />
            {timeAndMaterial && (
              <>
                <TextField
                  label="Not-to-exceed rate ($)" value={form.nteRate} inputMode="decimal" onChange={(v) => set('nteRate', v)}
                  help="Optional. Dollars, like 85.00. Choose what it is per beside it."
                />
                <label className="ct-field">
                  <span>Rate is</span>
                  <select value={form.nteRateUnit} onChange={(e) => set('nteRateUnit', e.target.value as '' | NteRateUnit)}>
                    <option value="">Choose…</option>
                    {NTE_RATE_UNIT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                  </select>
                  <span className="ct-help">The contract reads “Not to exceed rate: $85.00 per hour.”</span>
                </label>
              </>
            )}
            <TextField
              label="Monthly billing day" value={form.billingDay} inputMode="numeric" maxLength={2}
              onChange={(v) => set('billingDay', v)} help="The day of the month, 1 to 31."
            />
          </div>
        </div>
      </section>

      <section className="ct-rq-section" aria-labelledby="rq-scope">
        <h2 id="rq-scope">Scope of work</h2>
        <div className="ct-form">
          <TextArea
            label="Scope includes" rows={9} maxLength={20_000} value={form.scope} onChange={(v) => set('scope', v)}
            help="Each line becomes its own paragraph in the contract, so type it as you want it to read, numbering included."
          />
          <TextArea
            label="Excluded from scope" rows={4} maxLength={20_000} value={form.exclusions} onChange={(v) => set('exclusions', v)}
            help="Leave blank if nothing is excluded."
          />
        </div>
      </section>

      <section className="ct-rq-section" aria-labelledby="rq-bonds">
        <h2 id="rq-bonds">Payroll and bonds</h2>
        <div className="ct-form">
          <YesNoField label="Prevailing wage project" value={form.prevailingWage} onChange={(v) => set('prevailingWage', v)} />
          <div className="ct-grid2">
            <YesNoField label="Performance bond" value={form.performanceBond} onChange={(v) => set('performanceBond', v)} />
            {form.performanceBond === 'yes' && (
              <TextField label="Performance bond amount ($)" value={form.performanceBondAmount} inputMode="decimal" onChange={(v) => set('performanceBondAmount', v)} />
            )}
          </div>
          <div className="ct-grid2">
            <YesNoField label="Payment bond" value={form.paymentBond} onChange={(v) => set('paymentBond', v)} />
            {form.paymentBond === 'yes' && (
              <TextField label="Payment bond amount ($)" value={form.paymentBondAmount} inputMode="decimal" onChange={(v) => set('paymentBondAmount', v)} />
            )}
          </div>
        </div>
      </section>

      <section className="ct-rq-section" aria-labelledby="rq-schedule">
        <h2 id="rq-schedule">Schedule and liquidated damages</h2>
        <div className="ct-form">
          <div className="ct-grid2">
            <div className="ct-form">
              <DateField label="Project start date" value={form.startDate} max={LATEST_DAY} onChange={(v) => set('startDate', v)} />
              <label className="ct-field ct-field--check">
                <input type="checkbox" checked={form.startAsDirected} onChange={(e) => set('startAsDirected', e.target.checked)} />
                <span>Add “or As Directed by E&amp;W” after the start date</span>
              </label>
            </div>
            <div className="ct-form">
              <DateField label="Project completion date" value={form.completionDate} max={LATEST_DAY} onChange={(v) => set('completionDate', v)} />
              <label className="ct-field ct-field--check">
                <input type="checkbox" checked={form.completionAsDirected} onChange={(e) => set('completionAsDirected', e.target.checked)} />
                <span>Add “or As Directed by E&amp;W” after the completion date</span>
              </label>
            </div>
          </div>
          <YesNoField label="Liquidated damages" value={form.liquidatedDamages} onChange={(v) => set('liquidatedDamages', v)} />
          {form.liquidatedDamages === 'yes' && (
            <div className="ct-grid2">
              <TextField label="Liquidated damages amount ($)" value={form.liquidatedDamagesAmount} inputMode="decimal" onChange={(v) => set('liquidatedDamagesAmount', v)} />
              <label className="ct-field">
                <span>How often</span>
                <select
                  value={form.liquidatedDamagesFrequency}
                  onChange={(e) => set('liquidatedDamagesFrequency', e.target.value as RequestForm['liquidatedDamagesFrequency'])}
                >
                  <option value="">Choose…</option>
                  {LD_FREQUENCY_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
                <span className="ct-help">The contract reads “$2,000.00 per day”.</span>
              </label>
            </div>
          )}
        </div>
      </section>

      <section className="ct-rq-section" aria-labelledby="rq-requirements">
        <h2 id="rq-requirements">Requirements</h2>
        <div className="ct-questions">
          {prefill.requirements.map((q) => {
            const line = (DESCRIBED_BY as Record<string, keyof RequestForm['descriptions']>)[q.key];
            return (
              <div key={q.key} className={`ct-form${line && form.requirements[q.key] === 'yes' ? ' ct-question--wide' : ''}`}>
                <YesNoField label={q.label} value={form.requirements[q.key] ?? ''} onChange={(v) => setAnswer(q.key, v)} />
                {line && form.requirements[q.key] === 'yes' && (
                  <TextArea
                    label={DESCRIPTION_LABELS[line]} rows={3} maxLength={5_000}
                    value={form.descriptions[line]} onChange={(v) => setDescription(line, v)}
                    help="Leave blank to print nothing."
                  />
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="ct-rq-section" aria-labelledby="rq-documents">
        <h2 id="rq-documents">Documents to include</h2>
        <p className="ct-help">
          Optional. Anything the subcontract should go out with, such as an exhibit, a scope sheet or a bond form.
          {editing
            ? ' Add or remove them on the request’s own page.'
            : ' A request has to be saved before it can hold documents, so add them on the page that opens next.'}
        </p>
      </section>

      {error && <div className="ct-error" role="alert">{error}</div>}
      <div className="ct-actions">
        <Button variant="primary" onClick={onSave} isLoading={save.isPending} disabled={save.isPending}>
          {isDraft ? 'Save draft' : 'Save changes'}
        </Button>
        <Button variant="secondary" onClick={() => navigate(backTo)} disabled={save.isPending}>Cancel</Button>
        <span className="ct-help">
          {isDraft
            ? 'You can save with fields still empty and finish later. The next page lists what a submit still needs.'
            : 'You can leave a field as it is.'}
        </span>
      </div>
    </div>
  );
}
